import { useEffect, useRef, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Index from "./pages/Index";
import Login from "./pages/Login";
import { SupabaseAdminDashboard } from "./admin/components/SupabaseAdminDashboard";
import { SupabaseAdminLogin } from "./admin/components/SupabaseAdminLogin";
import NotFound from "./pages/NotFound";
import Guide from "./pages/Guide";
import Legal from "./pages/Legal";
import Shinu from "./pages/Shinu";
import SentryTest from "./pages/SentryTest";
import { supabase } from "@/lib/supabase";
import { PerformanceProvider } from "@/contexts/PerformanceContext";
import { DPLIcon } from "@/components/brand/DPLLogo";
import { useQuotationStore } from "./stores/quotationStore";
import { useBdmStore } from "./stores/designConsultantStore";
import { performanceMonitor } from "@/lib/utils/performanceMonitor";
import { logger } from "@/lib/utils/logger";
import { NetworkStatus } from "@/components/layout/NetworkStatus";
import AdminLayout from "./admin/AdminShell";
import TenantLayout from "./tenant/TenantShell";
import AdminApp from "./admin/App";
import { authService } from "@/services/authService";
import { AdminRouteGuard, TenantRouteGuard, AuthenticatedGuard } from "@/components/auth/RouteGuard";
import type { BootstrapResult } from "@/types/auth";
import {
  DEFAULT_INDUSTRY_SLUG,
  INDUSTRY_OPTIONS,
  normalizeIndustrySlug,
} from "@/data/industryOptions";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 5 * 60 * 1000 },
  },
});

// ── Role-based routing resolver ────────────────────────────────────────────────────
// Determines which shell to load based on user role using the new bootstrap system
async function resolveUserRole(): Promise<'admin' | 'tenant' | 'needs_setup' | 'error'> {
  try {
    // Use the new bootstrap system that properly separates identity domains
    const bootstrapResult: BootstrapResult = await authService.initializeApp();

    if (!bootstrapResult.success) {
      logger.error('[App] Bootstrap failed:', bootstrapResult.error);
      return 'error';
    }

    const context = bootstrapResult.context;
    if (!context) {
      return 'error';
    }

    // Route based on the properly detected role
    switch (context.role) {
      case 'admin':
        return 'admin';
      case 'tenant':
        return 'tenant';
      case 'unassigned':
        return 'needs_setup';
      default:
        logger.error('[App] Unknown role detected:', context.role);
        return 'error';
    }

    return 'tenant';
  } catch (error) {
    console.error('[App] Failed to resolve user role:', error);
    return 'error';
  }
}

// ── Slug helper ───────────────────────────────────────────────────────────────
// Must mirror sanitizeSlug() in tenantService — the backend re-sanitizes anyway,
// but deriving the same slug here means the preview shown to the user is accurate.
function deriveSlug(companyName: string): string {
  return companyName
    .toLowerCase()
    .replace(/[\s_]+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-{2,}/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 30)
    || 'workspace';
}

// ── WorkspaceSetup ────────────────────────────────────────────────────────────
// Handles the cross-browser magic-link onboarding gap:
//   • Same browser  → reads localStorage (dew_pending_setup)
//   • Cross-browser → reads Supabase user_metadata (set at signUp time, server-side)
//   • Both missing  → shows inline recovery form so the user can type their details
//
// Slug collisions are retried automatically inside tenantService.createTenant.
const WorkspaceSetup = ({ onComplete }: { onComplete: () => void }) => {
  const [status, setStatus] = useState<'creating' | 'needs-info' | 'catalog-choice' | 'done' | 'error'>('creating');
  const [message, setMessage] = useState('Initializing your workspace…');
  const [errorDetail, setErrorDetail] = useState('');
  const [recoveryForm, setRecoveryForm] = useState({ companyName: '', plan: 'starter', accountType: 'company' as 'company' | 'individual', industrySlug: DEFAULT_INDUSTRY_SLUG });
  const [recovering, setRecovering] = useState(false);
  const [newTenantId, setNewTenantId] = useState<string | null>(null);
  const [setupIndustrySlug, setSetupIndustrySlug] = useState<string>(DEFAULT_INDUSTRY_SLUG);

  const runSetup = async (overrideName?: string, overridePlan?: string, overrideAccountType?: 'company' | 'individual', overrideIndustrySlug?: string) => {
    try {
      const { tenantService } = await import('@/services/tenantService');

      // This component only renders when the user is authenticated but has no tenant.
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { setStatus('error'); setErrorDetail('Session expired. Please log in again.'); return; }

      // ── Idempotency pre-check ───────────────────────────────────────────────
      // In case of page refresh or multi-tab, check if the tenant was already
      // created between the ProtectedRoute check and now. If so, just proceed.
      const existingTenant = await tenantService.getCurrentTenant();
      if (existingTenant) {
        setMessage('Workspace ready. Loading dashboard…');
        setStatus('done');
        setTimeout(onComplete, 800);
        return;
      }

      const metadata = session?.user?.user_metadata || {};

      // Priority: localStorage (same-browser) → user_metadata (cross-browser) → override (recovery form)
      const raw = localStorage.getItem('dew_pending_setup');
      const pending = raw ? JSON.parse(raw) : null;

      logger.log(`[WorkspaceSetup] Starting — localStorage: ${!!pending}, metadata: ${!!metadata.company_name}, override: ${!!overrideName}`);
      const companyName  = overrideName         || pending?.companyName  || metadata.company_name;
      const plan         = overridePlan         || pending?.plan         || metadata.plan         || 'starter';

      // If we still have no company name, show the recovery form instead of
      // silently creating a "My Workspace" tenant with a meaningless slug.
      if (!companyName) {
        logger.log('[WorkspaceSetup] No company name found — showing recovery form');
        setStatus('needs-info');
        return;
      }

      const slug = pending?.nickname || metadata.company_nickname || deriveSlug(companyName);

      const accountType  = (pending?.accountType  || metadata.account_type  || 'company') as 'company' | 'individual';
      const industrySlug = normalizeIndustrySlug(
        pending?.industrySlug || metadata.industry_slug || DEFAULT_INDUSTRY_SLUG,
      );
      setSetupIndustrySlug(industrySlug);

      setMessage(`Creating workspace for ${companyName}…`);
      setStatus('creating');
      const tenant = await tenantService.createTenant(companyName, slug, plan, { accountType, industrySlug });
      setNewTenantId(tenant.id);

      localStorage.removeItem('dew_pending_setup');

      logger.log('[WorkspaceSetup] Tenant created successfully, moving to catalog choice');
      setStatus('catalog-choice');
    } catch (err: unknown) {
      console.error('[WorkspaceSetup] Workspace creation failed:', err);
      
      // Extract meaningful error message
      let errorMsg = 'Setup failed. Please contact support.';
      if (err) {
        if (err instanceof Error && err.message) {
          errorMsg = err.message;
        } else if (typeof err === 'object' && 'message' in err && err.message) {
          errorMsg = String(err.message);
        } else if (typeof err === 'string') {
          errorMsg = err;
        }
      }
      
      setErrorDetail(errorMsg);
      setStatus('error');
    }
  };

  const handleCatalogChoice = async (choice: 'starter' | 'clean') => {
    if (!newTenantId) return;
    try {
      setMessage(choice === 'starter' ? 'Cloning starter catalogues…' : 'Preparing clean workspace…');
      setStatus('creating');

      if (choice === 'starter') {
        try {
          const { tenantService } = await import('@/services/tenantService');
          await tenantService.onboardTenant(newTenantId, [setupIndustrySlug]);
        } catch (error) {
          logger.warn('[WorkspaceSetup] Failed to clone starter catalog:', error);
          // Non-fatal, just continue
        }
      }

      setMessage('Workspace ready. Loading dashboard…');
      setStatus('done');
      setTimeout(onComplete, 800);
    } catch (err) {
      console.error('[WorkspaceSetup] Catalog choice failed:', err);
      setErrorDetail('Failed to prepare catalog. Please try again or refresh.');
      setStatus('error');
    }
  };

  useEffect(() => {
    runSetup();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRecoverySubmit = async () => {
    if (!recoveryForm.companyName.trim()) return;
    setRecovering(true);
    await runSetup(recoveryForm.companyName.trim(), recoveryForm.plan, recoveryForm.accountType, recoveryForm.industrySlug);
    setRecovering(false);
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-8 relative overflow-hidden">
      <div className="absolute w-[700px] h-[700px] rounded-full bg-emerald-600/5 blur-[120px] -top-[350px] -left-[350px]" />
      <div className="absolute w-[500px] h-[500px] rounded-full bg-emerald-600/5 blur-[120px] -bottom-[250px] -right-[250px]" />

      <div className="relative z-10 flex flex-col items-center gap-6 animate-in fade-in zoom-in-95 duration-700 w-full max-w-sm px-6">
        <div className="relative">
          <div className={`absolute -inset-4 rounded-full blur-2xl transition-all duration-1000 ${status === 'error' ? 'bg-red-500/20' : 'bg-emerald-500/20 animate-pulse'}`} />
          <DPLIcon size={56} className="relative z-10" />
        </div>

        {/* ── Creating / done ── */}
        {(status === 'creating' || status === 'done') && (
          <>
            <div className="flex flex-col items-center gap-2 text-center">
              <h2 className="text-foreground font-semibold text-xl tracking-[0.15em] uppercase font-display">Workspace Setup</h2>
              <p className="text-xs font-medium uppercase tracking-[0.3em] text-emerald-500/60">{message}</p>
            </div>
            <div className="flex gap-2">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-1 w-8 rounded-full bg-muted/50 overflow-hidden">
                  <div className="h-full bg-emerald-600 animate-loading-bar" style={{ animationDelay: `${i * 0.2}s` }} />
                </div>
              ))}
            </div>
          </>
        )}

        {/* ── Catalog Choice ── */}
        {status === 'catalog-choice' && (
          <div className="w-full flex flex-col gap-6">
            <div className="text-center">
              <h2 className="text-foreground font-semibold text-xl tracking-[0.15em] uppercase font-display mb-2">How would you like to start?</h2>
              <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-[0.2em] px-4">
                Choose whether to begin with our standard commercial catalog or start with a clean workspace to build your own.
              </p>
            </div>
            
            <div className="flex flex-col gap-3">
              <button
                onClick={() => handleCatalogChoice('starter')}
                className="flex flex-col items-center justify-center p-6 bg-emerald-500/10 border border-emerald-500/30 rounded-2xl hover:bg-emerald-500/20 hover:border-emerald-500/50 transition-all group"
              >
                <span className="text-emerald-400 font-semibold tracking-widest uppercase text-sm mb-1 group-hover:scale-105 transition-transform">Use Starter Catalogues</span>
                <span className="text-emerald-500/60 text-[10px] font-medium uppercase tracking-widest text-center">Industry-standard line items & pricing</span>
              </button>
              
              <button
                onClick={() => handleCatalogChoice('clean')}
                className="flex flex-col items-center justify-center p-6 bg-black/40 border border-border rounded-2xl hover:bg-muted/50 hover:border-border transition-all group"
              >
                <span className="text-foreground font-semibold tracking-widest uppercase text-sm mb-1 group-hover:scale-105 transition-transform">Start Clean</span>
                <span className="text-muted-foreground text-[10px] font-medium uppercase tracking-widest text-center">Empty workspace to build from scratch</span>
              </button>
            </div>
          </div>
        )}

        {/* ── Recovery form — shown when metadata is blank (cross-browser gap) ── */}
        {status === 'needs-info' && (
          <div className="w-full flex flex-col gap-5">
            <div className="text-center">
              <h2 className="text-foreground font-semibold text-xl tracking-[0.15em] uppercase font-display mb-1">One More Step</h2>
              <p className="text-muted-foreground text-[10px] font-medium uppercase tracking-[0.2em]">
                We couldn't retrieve your signup details. Please confirm them to set up your workspace correctly.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Company / Professional Name</label>
                <input
                  type="text"
                  placeholder="DCODE Private Limited"
                  value={recoveryForm.companyName}
                  onChange={e => setRecoveryForm(f => ({ ...f, companyName: e.target.value }))}
                  className="h-12 px-4 rounded-xl bg-black/40 border border-border text-foreground text-sm placeholder:text-slate-700 focus:outline-none focus:border-emerald-500/40"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Plan</label>
                <div className="grid grid-cols-2 gap-2">
                  {(['starter', 'pro'] as const).map(p => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setRecoveryForm(f => ({ ...f, plan: p }))}
                      className={`h-10 rounded-xl text-[10px] font-semibold uppercase tracking-widest transition-all border ${
                        recoveryForm.plan === p
                          ? 'bg-emerald-600 border-emerald-500 text-white'
                          : 'bg-black/40 border-border text-muted-foreground hover:border-emerald-500/30'
                      }`}
                    >
                      {p === 'starter' ? 'Starter (Free)' : 'Pro (₹2,499/mo)'}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-semibold text-muted-foreground uppercase tracking-widest">Your Industry</label>
                <div className="grid grid-cols-2 gap-2">
                  {INDUSTRY_OPTIONS/*
                    { slug: 'interior-design', label: 'Interior Design', icon: '🛋️' },
                    { slug: 'civil-construction', label: 'Civil Construction', icon: '🏗️' },
                    { slug: 'landscape', label: 'Landscape', icon: '🌿' },
                    { slug: 'architecture', label: 'Architecture', icon: '📐' },
                    { slug: 'photography', label: 'Photography', icon: '📸' },
                    { slug: 'events', label: 'Events', icon: '🎪' },
                    { slug: 'it-consulting', label: 'IT Consulting', icon: '💻' },
                    { slug: 'furniture', label: 'Furniture', icon: '🪑' },
                  */.map(ind => (
                    <button
                      key={ind.slug}
                      type="button"
                      onClick={() => setRecoveryForm(f => ({ ...f, industrySlug: ind.slug }))}
                      className={`min-h-10 rounded-xl text-[10px] font-semibold uppercase tracking-widest transition-all border flex flex-col items-start justify-center gap-1 px-3 py-2 text-left ${
                        recoveryForm.industrySlug === ind.slug
                          ? 'bg-emerald-600 border-emerald-500 text-white'
                          : 'bg-black/40 border-border text-muted-foreground hover:border-emerald-500/30'
                      }`}
                    >
                      <span>{ind.label}</span>
                      <span className="text-[8px] font-medium uppercase tracking-wider text-inherit/70">{ind.description}</span>
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <button
              onClick={handleRecoverySubmit}
              disabled={recovering || !recoveryForm.companyName.trim()}
              className="w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white font-semibold text-[10px] uppercase tracking-widest transition-all active:scale-95"
            >
              {recovering ? 'Creating workspace…' : 'Continue →'}
            </button>
          </div>
        )}

        {/* ── Hard error ── */}
        {status === 'error' && (
          <>
            <div className="flex flex-col items-center gap-2 text-center">
              <h2 className="text-foreground font-semibold text-xl tracking-[0.15em] uppercase font-display">Setup Failed</h2>
              <p className="text-red-400 text-[10px] font-medium uppercase tracking-[0.2em] max-w-xs">{errorDetail}</p>
            </div>
            <button
              onClick={() => { window.location.href = '/login'; }}
              className="text-muted-foreground hover:text-emerald-400 text-[10px] font-semibold uppercase tracking-[0.3em] transition-all mt-2"
            >
              ← Return to Login
            </button>
          </>
        )}
      </div>
    </div>
  );
};

// ── ProtectedRoute ────────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [session, setSession] = useState<boolean | null>(null);
  const [needsSetup, setNeedsSetup] = useState(false);
  // hasFetchedRef: true once a successful full init has completed for the current session.
  const hasFetchedRef = useRef(false);
  // isInitializingRef: true while runAppInitialization() is in-flight.
  // Prevents a second concurrent call if onAuthStateChange fires during the initial getSession() init.
  const isInitializingRef = useRef(false);

  useEffect(() => {
    performanceMonitor.setSlowQueryThreshold(1500);

    // Purge legacy localStorage key from an older version of the app
    localStorage.removeItem('quotation-storage');

    const initializeApp = async () => {
      // Check if we're on an admin route - skip tenant init if we are!
      const isAdminRoute = window.location.pathname.startsWith('/admin');
      
      // Guards: skip if already done, already in-flight, OR on admin route!
      if (hasFetchedRef.current || isInitializingRef.current || isAdminRoute) {
        if (isAdminRoute) {
          // For admin routes, just mark as initialized so we don't keep trying
          hasFetchedRef.current = true;
        }
        return;
      }
      
      isInitializingRef.current = true;

      try {
        const result = await resolveUserRole();
        if (result === 'needs_setup') {
          setNeedsSetup(true);
        } else if (result === 'admin') {
          // Admin user - no tenant setup needed
          hasFetchedRef.current = true;
        } else if (result === 'tenant') {
          // Tenant user - properly initialized
          hasFetchedRef.current = true;
        } else {
          // Error case - still allow app to load
          hasFetchedRef.current = true;
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(!!session);
      if (session) initializeApp();
    }).catch(err => console.error('[App] getSession failed:', err));

    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      setSession(!!session);

      if (event === 'SIGNED_IN' && session) {
        // Reset flags so the new session gets a fresh init
        hasFetchedRef.current = false;
        
        // Clear auth service cache to force a fresh bootstrap promise
        authService.clearCache();
        
        initializeApp();
      } else if (event === 'SIGNED_OUT') {
        hasFetchedRef.current = false;
        isInitializingRef.current = false;
        setNeedsSetup(false);

        // Clear all service caches so the next user to sign in on this
        // tab does not briefly receive the previous user's data.
        const { tenantService } = await import('@/services/tenantService');
        const { authService } = await import('@/services/authService');
        const { settingsService } = await import('@/services/settingsService');
        
        tenantService.clearCache();
        authService.reset();
        settingsService.resetCache();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  // ── Session still resolving ───────────────────────────────────────────────
  if (session === null) {
    return (
      <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-12 relative overflow-hidden">
        <div className="absolute w-[800px] h-[800px] rounded-full bg-emerald-600/5 blur-[120px] pointer-events-none -top-[400px] -left-[400px]" />
        <div className="absolute w-[600px] h-[600px] rounded-full bg-emerald-600/5 blur-[120px] pointer-events-none -bottom-[300px] -right-[300px]" />

        <div className="relative z-10 flex flex-col items-center gap-8 animate-in fade-in zoom-in-95 duration-1000">
          <div className="relative group">
            <div className="absolute -inset-4 bg-emerald-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-1000" />
            <DPLIcon size={64} className="relative z-10" />
          </div>
          <div className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-emerald-500/20" />
              <span className="text-foreground font-semibold text-2xl tracking-[0.2em] uppercase font-display">DCODE DEW</span>
              <span className="h-px w-8 bg-emerald-500/20" />
            </div>
            <span className="text-emerald-500/40 text-[9px] tracking-[0.4em] uppercase font-semibold">Secure Workspace Initialization</span>
          </div>
        </div>

        <div className="relative z-10 flex flex-col items-center gap-4">
          <div className="flex gap-2">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="h-1 w-8 rounded-full bg-muted/50 overflow-hidden">
                <div className="h-full bg-emerald-600 animate-loading-bar" style={{ animationDelay: `${i * 0.2}s` }} />
              </div>
            ))}
          </div>
          <p className="text-slate-700 text-[10px] font-semibold uppercase tracking-[0.3em] animate-pulse">Verifying Session</p>
        </div>

        <div className="absolute bottom-12 left-0 w-full flex justify-center opacity-20">
          <p className="text-[8px] font-semibold uppercase tracking-[0.6em] text-foreground">DCODE CORPORATE DIGITAL INFRASTRUCTURE</p>
        </div>
      </div>
    );
  }

  // ── Not authenticated ─────────────────────────────────────────────────────
  if (!session) return <Navigate to="/login" replace />;

  // ── Authenticated but no tenant — run workspace setup ────────────────────
  if (needsSetup) {
    return (
      <WorkspaceSetup
        onComplete={async () => {
          // Workspace created — run the shared initializer, same as normal login
          setNeedsSetup(false);
          hasFetchedRef.current = false;
          isInitializingRef.current = false;

          const result = await resolveUserRole();
          if (result !== 'needs_setup') {
            hasFetchedRef.current = true;
          }
        }}
      />
    );
  }

  return <>{children}</>;
};

// ── Role-based App Router ───────────────────────────────────────────────────────
const RoleBasedRouter = () => {
  const [userRole, setUserRole] = useState<'admin' | 'tenant' | 'needs_setup' | 'error' | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const determineRoute = async () => {
      try {
        const role = await resolveUserRole();
        setUserRole(role);
        setLoading(false);
      } catch (error) {
        console.error('[Router] Failed to determine route:', error);
        setUserRole('error');
        setLoading(false);
      }
    };

    determineRoute();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-foreground text-center space-y-4">
          <div className="text-lg">Determining access level...</div>
          <div className="text-sm text-muted-foreground">If this takes too long, please refresh the page</div>
        </div>
      </div>
    );
  }

  console.log('[Router] Rendering for role:', userRole);

  switch (userRole) {
    case 'admin':
      console.log('[Router] Rendering AdminLayout');
      return <AdminLayout />;
    case 'tenant':
      console.log('[Router] Rendering TenantLayout');
      return <TenantLayout />;
    case 'needs_setup':
      console.log('[Router] Rendering WorkspaceSetup');
      return <WorkspaceSetup onComplete={() => window.location.reload()} />;
    case 'error':
    default:
      console.log('[Router] Navigating to login');
      return <Navigate to="/login" replace />;
  }
};

// ── Auth Listener ─────────────────────────────────────────────────────────────
const AuthListener = () => {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      if (event === 'SIGNED_IN' && session) {
        authService.clearCache();
      } else if (event === 'SIGNED_OUT') {
        const { tenantService } = await import('@/services/tenantService');
        const { authService: lazyAuth } = await import('@/services/authService');
        const { settingsService } = await import('@/services/settingsService');
        
        tenantService.clearCache();
        lazyAuth.reset();
        settingsService.resetCache();
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  return null;
};

// ── App ───────────────────────────────────────────────────────────────────────
const App = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthListener />
      <PerformanceProvider>
        <TooltipProvider>
          <NetworkStatus />
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/guide" element={<Guide />} />
              <Route path="/legal/:tab" element={<Legal />} />
              <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
              <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />
              <Route path="/security" element={<Navigate to="/legal/security" replace />} />
              <Route path="/shinu" element={<Shinu />} />
              <Route path="/sentry-test" element={<SentryTest />} />
              
              {/* Admin routes - use separate AdminApp */}
              <Route path="/admin/*" element={<AdminApp />} />
              
              {/* Tenant routes */}
              <Route path="/app/*" element={<TenantLayout />} />
              
              {/* Root route - determines which shell to load */}
              <Route path="/" element={<RoleBasedRouter />} />
              <Route path="/?setup=1" element={<RoleBasedRouter />} />
              <Route path="/setup" element={<RoleBasedRouter />} />
              
              {/* Legacy admin routes - handled by separate admin app */}
              {/* Note: /admin routes are served by AdminApp.tsx, not this file */}
              
              {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </PerformanceProvider>
    </QueryClientProvider>
  );
};

export default App;
